<template>
  <Teleport to="#app">
    <div
      ref="cxTooltipElement"
      class="cx-tooltip"
      :style="{
        top: computedTop,
        left: computedLeft,
        'animation-delay': `${animationDelay}s`,
        maxWidth: computedMaxWidth
      }"
      :class="computedClass"
      v-if="show">
      <span class="cx-tooltip__title" v-if="text">{{ text }}</span>
    </div>
  </Teleport>
</template>

<script lang="ts" setup>
import { ref,
  onUpdated,
  computed,
  onMounted } from 'vue';
import { TooltipPosition } from './TooltipPosition.enum';
import { TooltipProps } from '@/components/tooltip/TooltipProps';

const props = withDefaults(defineProps<TooltipProps>(), {
  show: false,
  position: TooltipPosition.TopCenter,
  left: 0,
  top: 0,
  offsetTop: -3,
  offsetLeft: 0,
  animationDelay: 0.5
});

const cxTooltipElement = ref<HTMLElement | null>(null);

const tooltipWidth = ref(0);
const tooltipHeight = ref(0);
const parentWidth = ref(0);
const parentHeight = ref(0);

const calculateTooltipPosition = () => {
  const parent = props.parentElement || cxTooltipElement.value?.parentElement;
  if (!cxTooltipElement.value || !props.show) return;
  tooltipWidth.value = cxTooltipElement.value?.getBoundingClientRect().width || 0;
  tooltipHeight.value = cxTooltipElement.value?.getBoundingClientRect().height || 0;
  if (parent) {
    parentWidth.value = parent.getBoundingClientRect().width;
    parentHeight.value = parent.getBoundingClientRect().height;
  }
};
onMounted(calculateTooltipPosition);
onUpdated(calculateTooltipPosition);

const computedTop = computed(() => {
  if (!props.show) return '';
  switch (props.position) {
    case TooltipPosition.TopCenter:
    case TooltipPosition.TopLeft:
    case TooltipPosition.TopRight: {
      return props.top ? `${props.top - tooltipHeight.value + props.offsetTop}px` : 'none';
    }
    case TooltipPosition.BottomCenter:
    case TooltipPosition.BottomLeft:
    case TooltipPosition.BottomRight: {
      return props.top ? `${props.top + parentHeight.value - props.offsetTop}px` : 'none';
    }
    default:
      return '';
  }
});

const computedLeft = computed(() => {
  if (!props.show) return '';
  const parentHalf = parentWidth.value / 2.0;
  const selfHalf = tooltipWidth.value / 2.0;
  switch (props.position) {
    case TooltipPosition.TopCenter:
    case TooltipPosition.BottomCenter: {
      return `${props.left + parentHalf - selfHalf + props.offsetLeft}px`;
    }
    case TooltipPosition.TopLeft:
    case TooltipPosition.BottomLeft: {
      return `${props.left - props.offsetLeft}px`;
    }
    case TooltipPosition.TopRight:
    case TooltipPosition.BottomRight: {
      return `${props.left + parentHalf + props.offsetLeft}px`;
    }
    default:
      return '';
  }
});

const computedMaxWidth = computed(() => {
  if (props.maxWidth) return `${props.maxWidth}px`;
  return 'auto';
});

const computedClass = computed(() => {
  return {
    'cx-tooltip--wrap': !!props.maxWidth
  };
});

</script>

<style lang="less">
@import '../../common.less';

@keyframes cx-tooltip-show-tooltip {
  100% { opacity: 1; }
}

.cx-tooltip {
  z-index: 11;
  position: absolute;
  pointer-events: auto;
  width: auto;
  height: auto;
  background-color: @tooltipBGColor;
  font-size: 11px;
  border-radius: 8px;
  .flex(column, center, center);
  padding: 10px 14px 11px;
  color: @tooltipTextColor;
  opacity: 0;
  animation-name: cx-tooltip-show-tooltip;
  animation-duration: 0.2s;
  animation-fill-mode: forwards;
  text-align: center;
  white-space: nowrap;

  &__title {
    display: block;
    font-size: @appFontSize;
    font-weight: normal;
  }

  &--wrap {
    white-space: normal;
    word-break: break-word;
  }
}
</style>
